// src/components/McpConnections/ToolItem.js
import { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import { callTool } from './api';
import Spinner from './Spinner';
import { DataState } from '../../context/DataProvider';

// The isExpanded and onToggle props are new. They are controlled by the parent.
const ToolItem = ({ tool, connectionId, isExpanded, onToggle }) => {
  const { userGoogleId: accessToken } = DataState();
  const [formData, setFormData] = useState({});
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCallTool = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);

    const args = {};
    for (const key in formData) {
      const prop = tool.inputSchema?.properties?.[key];
      let value = formData[key];
      if (value !== '' && value !== undefined) {
        if (prop?.type === 'number') args[key] = parseFloat(value);
        else if (prop?.type === 'boolean') args[key] = Boolean(value);
        else args[key] = value;
      }
    }

    try {
      const response = await callTool(
        accessToken,
        connectionId,
        tool.name,
        args,
      );
      setResult({ data: response, isError: false });
    } catch (error) {
      const errorData = error.response?.detail || error.message;
      setResult({ data: errorData, isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  const schema = tool.inputSchema || { properties: {} };
  const properties = schema.properties || {};

  return (
    <li className="overflow-hidden rounded-lg border border-zinc-700 bg-zinc-800">
      {/* Make the header clickable to toggle expansion */}
      {/* eslint-disable-next-line */}
      <div
        className="flex cursor-pointer items-center justify-between p-4 hover:bg-zinc-700/50"
        onClick={onToggle}
      >
        <div>
          <p className="font-semibold text-gray-200">{tool.name}</p>
          <p className="text-sm text-gray-400">{tool.description}</p>
        </div>
        <span className="text-lg text-gray-400">
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>

      {/* The form and result are now rendered conditionally */}
      {isExpanded && (
        <div className="border-t border-zinc-700 p-4">
          <form onSubmit={handleCallTool}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {Object.keys(properties).map((key) => {
                // ... (the form field rendering logic is the same as before) ...
                const prop = properties[key];
                const isRequired = schema.required?.includes(key);
                const inputType =
                  prop.type === 'number'
                    ? 'number'
                    : prop.type === 'boolean'
                      ? 'checkbox'
                      : 'text';

                return (
                  <div key={key}>
                    <label className="text-xs font-medium text-gray-400">
                      {key}{' '}
                      {isRequired && <span className="text-red-500">*</span>}
                      <span className="ml-2 opacity-70">({prop.type})</span>
                    </label>
                    <input
                      type={inputType}
                      name={key}
                      checked={formData[key] || false}
                      onChange={handleInputChange}
                      placeholder={prop.description || ''}
                      className="mt-1 w-full rounded-md border border-zinc-600 bg-zinc-900 px-3 py-2 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex items-center gap-4">
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-zinc-600"
              >
                {isLoading && <Spinner size="1.2rem" />}
                Call Tool
              </button>
            </div>
          </form>
          {result && (
            <div className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-gray-300">
                Result:
              </h4>
              <pre
                className={`max-h-60 overflow-y-auto rounded-md p-4 text-xs ${result.isError ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'}`}
              >
                {JSON.stringify(result.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default ToolItem;
